import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fs } from '../firebase';
import { collection, serverTimestamp, doc, setDoc, updateDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import API_ENDPOINTS, { API_PROXY_BASE } from '../config/api';
import { sendToTelegram } from '../utils/telegramHelper';
import { isDeliveryMode } from '../utils/orderMode';
import { resolveOrderType } from '../utils/orderFulfillment';
import { resolveSyrvePaymentType } from '../utils/paymentTypes';
import { waitForFirebaseUser } from '../utils/authWait';
import { getLoyaltyPointsRate, calculateLoyaltyPoints } from '../utils/loyaltyRate';

const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error(`Request timeout after ${timeout/1000} seconds`);
        }
        throw error;
    }
};

const hasSfaDeliveryInCart = (ordersByVenue) => {
    const sfaKeys = ['SFA', 'SFA-Delivery', 'SFADelivery', 'sfa', 'sfa-delivery'];
    return Object.keys(ordersByVenue).some(key =>
        sfaKeys.some(sfaKey => key.toLowerCase().includes(sfaKey.toLowerCase()))
    );
};

const SFA_DELIVERY_CONFIG = {
    organizationId: 'cef12ab2-d9a5-4e3f-babb-11e6254849e8',
    terminalGroupId: '826d527c-bc55-495c-9ce2-43369609512e',
    name: 'SFA - Delivery'
};

const awardLoyaltyPoints = async (params) => {
    const {
        token,
        organizationId,
        phoneNumber,
        points,
        orderType,
        firebaseOrderId,
        apiProxyBase
    } = params;

    try {
        if (!phoneNumber || phoneNumber.trim() === '') {
            return { success: false, reason: 'no_phone' };
        }

        let formattedPhone = phoneNumber.trim();
        if (!formattedPhone.startsWith('+')) {
            formattedPhone = '+995' + formattedPhone.replace(/\D/g, '');
        }

        const customerInfoRes = await fetch(`${apiProxyBase}/loyalty/syrve/customer/info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                phone: formattedPhone,
                organizationId
            })
        });

        if (!customerInfoRes.ok) {
            return { success: false, reason: 'customer_not_found' };
        }

        const customerData = await customerInfoRes.json();
        const customerId = customerData.id;
        const walletBalances = customerData.walletBalances || [];
        const bonusWallet = walletBalances.find(w => w.type === 1);

        if (!bonusWallet || !bonusWallet.id) {
            return { success: false, reason: 'no_wallet' };
        }

        const walletId = bonusWallet.id;

        const topupRes = await fetch(`${apiProxyBase}/loyalty/syrve/customer/wallet/topup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                customerId,
                walletId,
                sum: points,
                comment: `Order points: ${Math.floor(points)} (Order: ${firebaseOrderId || 'N/A'})`,
                organizationId
            })
        });

        if (!topupRes.ok) {
            const errorText = await topupRes.text();
            return { success: false, reason: 'topup_failed', error: errorText };
        }

        const topupResult = await topupRes.json();

        return {
            success: true,
            customerId,
            walletId,
            pointsAwarded: points,
            newBalance: bonusWallet.balance + points,
            result: topupResult
        };
    } catch (error) {
        console.error('[LOYALTY-ERROR] Error awarding loyalty points:', error);
        return { success: false, reason: 'exception', error: error.message };
    }
};

const PaymentReturn = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [processing, setProcessing] = useState(true);
    const [message, setMessage] = useState('Processing payment...');
    const auth = getAuth();

    useEffect(() => {
        const processPaymentReturn = async () => {
            try {
                const status = searchParams.get('status');
                const orderId = searchParams.get('orderId');
                
                // Get stored payment data
                const pendingData = localStorage.getItem('pendingBogPayment');
                const pendingBogOrderId = localStorage.getItem('pendingBogOrderId');
                const bogToken = localStorage.getItem('bogToken');
                
                if (!pendingData) {
                    setMessage('No pending payment found');
                    setProcessing(false);
                    setTimeout(() => navigate('/'), 3000);
                    return;
                }

                const paymentData = JSON.parse(pendingData);
                
                // Verify the orderId matches
                if (paymentData.orderId !== orderId) {
                    setMessage('Order ID mismatch');
                    setProcessing(false);
                    setTimeout(() => navigate('/'), 3000);
                    return;
                }

                if (status === 'success') {
                    // Verify payment status with BOG API
                    const paymentVerified = await verifyPaymentStatus(pendingBogOrderId, bogToken);

                    if (paymentVerified) {
                        setMessage('Payment successful! Creating your order...');

                        // Process the order
                        try {
                            console.log('[ORDER-FLOW] Step 1: Creating Firebase order...');
                            await createOrder(paymentData);
                            console.log('[ORDER-FLOW] Step 1: ✅ Firebase order created');

                            console.log('[ORDER-FLOW] Step 2: Creating Syrve POS orders...');
                            await createSyrveOrders(paymentData);
                            console.log('[ORDER-FLOW] Step 2: ✅ Syrve orders created');
                        } catch (error) {
                            console.error('[ORDER-FLOW] ❌ FAILED:', error);

                            setMessage(`Order submission failed: ${error.message}. Please contact support with your payment confirmation.`);
                            setProcessing(false);

                            return;
                        }
                        
                        // Clean up localStorage
                        localStorage.removeItem('pendingBogPayment');
                        localStorage.removeItem('pendingBogOrderId');
                        localStorage.removeItem('bogToken');
                        localStorage.removeItem('cartByCompany');
                        localStorage.removeItem('productQuantities');
                        localStorage.setItem("notification", JSON.stringify(true));
                        
                        setMessage('Order created successfully!');
                        setTimeout(() => navigate('/'), 2000);
                    } else {
                        setMessage('Payment verification failed');
                        setProcessing(false);
                        setTimeout(() => navigate('/'), 3000);
                    }
                } else {
                    // Payment failed or cancelled
                    setMessage('Payment was cancelled or failed');
                    
                    // Clean up localStorage
                    localStorage.removeItem('pendingBogPayment');
                    localStorage.removeItem('pendingBogOrderId');
                    localStorage.removeItem('bogToken');
                    
                    setProcessing(false);
                    setTimeout(() => navigate('/'), 3000);
                }
            } catch (error) {
                console.error('Error processing payment return:', error);
                setMessage('An error occurred processing your payment');
                setProcessing(false);
                setTimeout(() => navigate('/'), 3000);
            }
        };

        processPaymentReturn();
    }, [searchParams, navigate]);

    const verifyPaymentStatus = async (orderBogId, bogToken) => {
        try {
            const response = await fetch(API_ENDPOINTS.proxy.bogReceipt(orderBogId), {
                headers: {
                    'Authorization': `Bearer ${bogToken}`,
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Network error: ${response.status}`);
            }

            const data = await response.json();
            const statusKey = data.order_status?.key;

            return statusKey === 'completed';
        } catch (error) {
            console.error('Error verifying payment status:', error);
            return false;
        }
    };

    const generateOrderId = (length = 16) => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const createOrder = async (paymentData) => {
        const user = await waitForFirebaseUser(auth, onAuthStateChanged);

        const { orderData, totalPrice, orderReceipt, productQuantities } = paymentData;
        const ordersByVenue = JSON.parse(localStorage.getItem("cartByCompany") || "{}");

        const products = [];
        const orderReceiptObject = {};

        Object.entries(ordersByVenue).forEach(([venue, items]) => {
            items.forEach(product => {
                const quantity = (productQuantities || {})[product.id] || 1;
                products.push({
                    productId: product.id,
                    quantity,
                    name: product.title || 'Unknown product',
                    price: product.price || 0,
                    company: venue,
                    modifiers: product.modifiers || [],
                });
            });

            orderReceiptObject[venue] = items.map(product => ({
                id: product.id,
                title: product.title || 'Unknown product',
                price: product.price || 0,
                quantity: (productQuantities || {})[product.id] || 1,
                modifiers: product.modifiers || []
            }));
        });

        const order = {
            ...orderData,
            orderReceipt: orderReceiptObject,
            orderType: resolveOrderType(orderData?.orderType, orderReceipt),
            totalPrice,
            products,
            userId: user.uid,
            recipientEmail: user.email,
            status: 'pending',
            createdAt: serverTimestamp(),
            paymentVerified: true,
            paymentStatus: 'completed',
            paymentMethod: 'online'
        };

        const orderId = generateOrderId();
        localStorage.setItem('firebaseOrderId', orderId);
        const orderRef = doc(collection(fs, 'DeliveryOrder'), orderId);
        order.id = orderId;

        await setDoc(orderRef, order);

        if (order.paymentVerified) {
            console.log('[TELEGRAM] Sending notification...');
            const telegramResult = await sendToTelegram(order);
            if (!telegramResult.success) {
                console.error('[TELEGRAM] ❌ Failed:', telegramResult.error);
            } else {
                console.log('[TELEGRAM] ✅ Notification sent successfully');
            }
        }
    };


    const createSyrveOrders = async (paymentData) => {
        const { orderData, productQuantities, totalPrice, orderReceipt } = paymentData;
        const ordersByVenue = JSON.parse(localStorage.getItem("cartByCompany") || "{}");

        const VENUE_NAME_MAP = {
            "Aromiitaliani": "Aromi Italiani",
            "CopaCabana": "Copacabana",
            "Nadimi": "Nadimi",
            "PoshNoshs": "Poshnosh",
            "Symposium": "Symposium 2"
        };

        try {
            const deliveryModeSelected = isDeliveryMode();
            const useSfaDeliveryForToken = deliveryModeSelected || hasSfaDeliveryInCart(ordersByVenue);
            const apiLogin = useSfaDeliveryForToken
                ? "ace87d94922a4f399ca68496897ba644"
                : "df27763ebfa044aa9ab31a11ff1ad042";

            console.log('[SYRVE] Using', useSfaDeliveryForToken ? 'SFA/AGGREGATOR' : 'INDIVIDUAL', 'API token');

            const tokenRes = await fetchWithTimeout(`${API_PROXY_BASE}/access_token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ apiLogin }),
            }, 10000);
            const tokenData = await tokenRes.json();
            const token = tokenData.token;

            if (!token || token.length === 0) {
                throw new Error('Failed to authenticate with Syrve POS');
            }

            const orgRes = await fetchWithTimeout(`${API_PROXY_BASE}/organizations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    organizationIds: [],
                    returnAdditionsInfo: true,
                    includeDisabled: false,
                }),
            }, 10000);

            const orgData = await orgRes.json();
            const allOrganizations = orgData.organizations;

            const useSfaDelivery = deliveryModeSelected || hasSfaDeliveryInCart(ordersByVenue);

            if (useSfaDelivery) {
                const allProducts = [];
                for (const [venueName, products] of Object.entries(ordersByVenue)) {
                    if (products && products.length > 0) {
                        products.forEach(product => {
                            allProducts.push({ ...product, sourceVenue: venueName });
                        });
                    }
                }

                if (allProducts.length === 0) {
                    console.warn('[SFA-DELIVERY] No products to order');
                    return;
                }

                const organizationId = SFA_DELIVERY_CONFIG.organizationId;
                const terminalGroupId = SFA_DELIVERY_CONFIG.terminalGroupId;

                const orderTypesUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/order_types`
                    : `${API_PROXY_BASE}/order-types`;

                const orderTypeRes = await fetchWithTimeout(orderTypesUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ organizationIds: [organizationId] }),
                }, 10000);
                const orderTypesData = await orderTypeRes.json();

                let orderTypeId;
                if (orderData.orderType === "delivery") {
                    const deliveryType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Доставка курьером" &&
                            item.orderServiceType === "DeliveryByCourier" &&
                            item.isDeleted === false
                    );
                    orderTypeId = deliveryType?.id;
                    if (!orderTypeId) throw new Error('Delivery order type not configured in POS');
                } else if (orderData.orderType === "self-pickup") {
                    const pickupType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            (item.name.toLowerCase().includes("самовывоз") ||
                                item.name.toLowerCase().includes("pickup") ||
                                item.name.toLowerCase().includes("take") ||
                                item.name === "Self-service") &&
                            item.orderServiceType === "DeliveryByClient" &&
                            item.isDeleted === false
                    );
                    orderTypeId = pickupType?.id;
                    if (!orderTypeId) throw new Error('Self-pickup order type not configured in POS');
                } else {
                    const dineIn = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Dine-In Order" &&
                            item.orderServiceType === "Common" &&
                            item.isDeleted === false
                    );
                    orderTypeId = dineIn?.id;
                    if (!orderTypeId) throw new Error('Dine-in order type not configured in POS');
                }

                const paymentRes = await fetchWithTimeout(`${API_PROXY_BASE}/payment-types`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, organizationIds: [organizationId] }),
                }, 10000);
                const paymentDataRes = await paymentRes.json();
                const paymentTypes = paymentDataRes?.paymentTypes || [];

                const {
                    paymentTypeId,
                    paymentTypeKind
                } = resolveSyrvePaymentType(paymentTypes, orderData.paymentMethod, 'SFA-Delivery');

                const orderItems = allProducts.map(product => ({
                    type: "Product",
                    amount: productQuantities[product.id] || 1,
                    productId: product.id,
                    modifiers: product.modifiers || []
                }));

                const aggregatorTotal = allProducts.reduce((sum, product) => {
                    return sum + (product.price * (productQuantities[product.id] || 1));
                }, 0);

                const payments = [{
                    paymentTypeKind,
                    sum: aggregatorTotal,
                    paymentTypeId,
                    isProcessedExternally: false,
                    isFiscalizedExternally: false,
                    isPrepay: false
                }];

                const now = new Date();
                const offsetMinutes = 30;
                const georgiaOffset = 4 * 60;
                const localTime = new Date(now.getTime() + offsetMinutes * 60 * 1000 + georgiaOffset * 60 * 1000);
                const completeBefore = localTime.toISOString().replace('T', ' ').replace('Z', '').split('.')[0] + '.000';

                let formattedPhone = orderData.phoneNumber.trim();
                if (!formattedPhone.startsWith('+')) {
                    formattedPhone = '+995' + formattedPhone.replace(/\D/g, '');
                }

                const baseOrder = {
                    id: crypto.randomUUID(),
                    phone: formattedPhone,
                    externalNumber: localStorage.getItem("firebaseOrderId"),
                    completeBefore,
                    customer: { name: orderData.recipientName, type: "one-time" },
                    items: orderItems,
                    payments,
                    comment: orderData.comment || '',
                    ...(orderData.tableNum ? { tableIds: [orderData.tableNum] } : {})
                };

                if (orderData.orderType === 'delivery') {
                    baseOrder.orderServiceType = "DeliveryByCourier";
                    if (!orderData.destination?.lat || !orderData.destination?.lng) {
                        throw new Error("Delivery requires lat and lng coordinates");
                    }
                    baseOrder.deliveryPoint = {
                        coordinates: { latitude: orderData.destination.lat, longitude: orderData.destination.lng },
                        address: {
                            street: { name: orderData.destination.address || 'Customer address' },
                            house: orderData.apartment?.trim() || '-',
                            flat: orderData.entrance?.trim() || '-',
                            type: "string"
                        },
                        externalCartographyId: orderData.destination?.externalCartographyId?.trim() || "-",
                        comment: (orderData.comment || '') + ` Phone: ${orderData.phoneNumber}` + (orderData.deliveryTime ? ` Time: ${orderData.deliveryTime}` : '')
                    };
                } else if (orderData.orderType === 'self-pickup') {
                    baseOrder.orderServiceType = "DeliveryByClient";
                } else {
                    baseOrder.orderTypeId = orderTypeId;
                    baseOrder.terminalGroupId = terminalGroupId;
                    baseOrder.guestCount = orderData.personsCount;
                }

                const orderPayload = {
                    organizationId,
                    terminalGroupId,
                    order: baseOrder,
                    createOrderSettings: { servicePrint: false, transportToFrontTimeout: 10, checkStopList: false }
                };

                const createOrderUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/create`
                    : `${API_PROXY_BASE}/create-order`;

                const createOrderRes = await fetchWithTimeout(createOrderUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, orderPayload }),
                }, 30000);

                if (!createOrderRes.ok) {
                    const errorText = await createOrderRes.text();
                    let errorData;
                    try { errorData = JSON.parse(errorText); } catch (e) {
                        throw new Error(`Syrve POS HTTP ${createOrderRes.status}: ${errorText}`);
                    }
                    throw new Error(errorData.error || `Syrve POS error: ${createOrderRes.status}`);
                }

                const createOrderResult = await createOrderRes.json();

                if (createOrderResult.error) {
                    throw new Error(createOrderResult.error || 'Syrve POS returned an error');
                }

                if (!createOrderResult.orderInfo?.id && !createOrderResult.correlationId) {
                    throw new Error('Syrve POS returned incomplete response (no order ID)');
                }

                const firebaseOrderId = localStorage.getItem('firebaseOrderId');
                if (firebaseOrderId) {
                    const orderRef = doc(fs, 'DeliveryOrder', firebaseOrderId);
                    const updateData = {
                        'syrveOrganizationIds.SFA-Delivery': organizationId,
                        orderSource: 'fastdelivery.ge',
                        isAggregatorOrder: true
                    };
                    if (createOrderResult.orderInfo?.id) {
                        updateData['syrveOrderIds.SFA-Delivery'] = createOrderResult.orderInfo.id;
                    }
                    if (createOrderResult.correlationId) {
                        updateData['syrveCorrelationIds.SFA-Delivery'] = createOrderResult.correlationId;
                    }
                    await updateDoc(orderRef, updateData);
                }

                const pointsToAward = calculateLoyaltyPoints(aggregatorTotal, await getLoyaltyPointsRate());
                if (pointsToAward > 0) {
                    const loyaltyResult = await awardLoyaltyPoints({
                        token,
                        organizationId,
                        phoneNumber: orderData.phoneNumber,
                        points: pointsToAward,
                        orderType: orderData.orderType,
                        firebaseOrderId: localStorage.getItem('firebaseOrderId'),
                        apiProxyBase: API_PROXY_BASE
                    });
                    if (loyaltyResult.success && firebaseOrderId) {
                        await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                            'loyaltyPointsAwarded.SFA-Delivery': pointsToAward,
                            'loyaltyAwardedAt.SFA-Delivery': serverTimestamp(),
                            'loyaltyCustomerId.SFA-Delivery': loyaltyResult.customerId,
                            'loyaltyWalletId.SFA-Delivery': loyaltyResult.walletId
                        });
                    }
                }

                return;
            }

            for (const [venueName, products] of Object.entries(ordersByVenue)) {
                if (!products || products.length === 0) continue;

                const org = allOrganizations.find(o => o.name === VENUE_NAME_MAP[venueName]);
                if (!org) {
                    console.error('[SYRVE] Organization not found for venue:', venueName, '(mapped:', VENUE_NAME_MAP[venueName], ')');
                    throw new Error(`Organization not found for venue: ${venueName} (mapped name: ${VENUE_NAME_MAP[venueName]})`);
                }

                const organizationId = org.id;

                const terminalRes = await fetchWithTimeout(`${API_PROXY_BASE}/terminalgroups`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ organizationIds: [organizationId] }),
                }, 10000);
                const terminalData = await terminalRes.json();
                const terminalGroupId = terminalData?.terminalGroups?.[0]?.items?.[0]?.id;

                const orderTypesUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/order_types`
                    : `${API_PROXY_BASE}/order-types`;

                const orderTypeRes = await fetchWithTimeout(orderTypesUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ organizationIds: [organizationId] }),
                }, 10000);
                const orderTypesData = await orderTypeRes.json();

                let orderTypeId;
                if (orderData.orderType === "delivery") {
                    const deliveryType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Доставка курьером" &&
                            item.orderServiceType === "DeliveryByCourier" &&
                            item.isDeleted === false
                    );
                    orderTypeId = deliveryType?.id;
                    if (!orderTypeId) throw new Error(`Delivery order type not configured in POS for ${venueName}`);
                } else if (orderData.orderType === "self-pickup") {
                    const pickupType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            (item.name.toLowerCase().includes("самовывоз") ||
                                item.name.toLowerCase().includes("pickup") ||
                                item.name.toLowerCase().includes("take") ||
                                item.name === "Self-service") &&
                            item.orderServiceType === "DeliveryByClient" &&
                            item.isDeleted === false
                    );
                    orderTypeId = pickupType?.id;
                    if (!orderTypeId) throw new Error(`Self-pickup order type not configured in POS for ${venueName}`);
                } else {
                    const dineIn = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Dine-In Order" &&
                            item.orderServiceType === "Common" &&
                            item.isDeleted === false
                    );
                    orderTypeId = dineIn?.id;
                    if (!orderTypeId) throw new Error(`Dine-in order type not configured in POS for ${venueName}`);
                }

                const paymentRes = await fetchWithTimeout(`${API_PROXY_BASE}/payment-types`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, organizationIds: [organizationId] }),
                }, 10000);
                const paymentDataRes = await paymentRes.json();
                const paymentTypes = paymentDataRes?.paymentTypes || [];

                const {
                    paymentTypeId,
                    paymentTypeKind
                } = resolveSyrvePaymentType(paymentTypes, orderData.paymentMethod, venueName);

                const orderItems = products.map(product => ({
                    type: "Product",
                    amount: productQuantities[product.id] || 1,
                    productId: product.id,
                    modifiers: product.modifiers || [],
                }));

                const venueTotal = products.reduce((sum, product) => {
                    return sum + (product.price * (productQuantities[product.id] || 1));
                }, 0);

                const payments = [{
                    paymentTypeKind,
                    sum: venueTotal,
                    paymentTypeId,
                    isProcessedExternally: false,
                    isFiscalizedExternally: false,
                    isPrepay: false
                }];

                const now = new Date();
                const offsetMinutes = 30;
                const georgiaOffset = 4 * 60;
                const localTime = new Date(now.getTime() + offsetMinutes * 60 * 1000 + georgiaOffset * 60 * 1000);
                const completeBefore = localTime.toISOString().replace('T', ' ').replace('Z', '').split('.')[0] + '.000';

                let formattedPhone = orderData.phoneNumber.trim();
                if (!formattedPhone.startsWith('+')) {
                    formattedPhone = '+995' + formattedPhone.replace(/\D/g, '');
                }

                const baseOrder = {
                    id: crypto.randomUUID(),
                    phone: formattedPhone,
                    externalNumber: localStorage.getItem("firebaseOrderId"),
                    completeBefore,
                    customer: {
                        name: orderData.recipientName,
                        type: "one-time",
                    },
                    items: orderItems,
                    payments,
                    comment: orderData.comment || '',
                    ...(orderData.tableNum ? { tableIds: [orderData.tableNum] } : {})
                };

                if (orderData.orderType === 'delivery') {
                    baseOrder.orderServiceType = "DeliveryByCourier";
                    if (!orderData.destination?.lat || !orderData.destination?.lng) {
                        throw new Error("Delivery requires lat and lng coordinates");
                    }
                    baseOrder.deliveryPoint = {
                        coordinates: {
                            latitude: orderData.destination.lat,
                            longitude: orderData.destination.lng
                        },
                        address: {
                            street: { name: orderData.destination.address || 'Customer address' },
                            house: orderData.apartment?.trim() || '-',
                            flat: orderData.entrance?.trim() || '-',
                            type: "string"
                        },
                        externalCartographyId: orderData.destination?.externalCartographyId?.trim() || "-",
                        comment: (orderData.comment || '') + ` Phone: ${orderData.phoneNumber}` + (orderData.deliveryTime ? ` Time: ${orderData.deliveryTime}` : '')
                    };
                } else if (orderData.orderType === 'self-pickup') {
                    baseOrder.orderServiceType = "DeliveryByClient";
                } else {
                    baseOrder.orderTypeId = orderTypeId;
                    baseOrder.terminalGroupId = terminalGroupId;
                    baseOrder.guestCount = orderData.personsCount;
                }

                const orderPayload = {
                    organizationId,
                    terminalGroupId,
                    order: baseOrder,
                    createOrderSettings: {
                        servicePrint: false,
                        transportToFrontTimeout: 10,
                        checkStopList: false
                    }
                };

                const createOrderUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/create`
                    : `${API_PROXY_BASE}/create-order`;

                const createOrderRes = await fetchWithTimeout(createOrderUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, orderPayload }),
                }, 30000);

                if (!createOrderRes.ok) {
                    const errorText = await createOrderRes.text();
                    let errorData;
                    try { errorData = JSON.parse(errorText); } catch (e) {
                        throw new Error(`${venueName}: Syrve POS HTTP ${createOrderRes.status}: ${errorText}`);
                    }
                    throw new Error(`${venueName}: ${errorData.error || `Syrve POS error ${createOrderRes.status}`}`);
                }

                const createOrderResult = await createOrderRes.json();
                console.log(`[SYRVE] Order in ${venueName} result:`, createOrderResult);

                if (createOrderResult.error) {
                    throw new Error(`${venueName}: ${createOrderResult.error}`);
                }

                if (!createOrderResult.orderInfo?.id && !createOrderResult.correlationId) {
                    throw new Error(`${venueName}: Syrve POS returned incomplete response`);
                }

                if (createOrderResult.orderInfo?.id || createOrderResult.correlationId) {
                    const firebaseOrderId = localStorage.getItem('firebaseOrderId');
                    if (firebaseOrderId) {
                        const orderRef = doc(fs, 'DeliveryOrder', firebaseOrderId);
                        const updateData = {
                            [`syrveOrganizationIds.${venueName}`]: organizationId
                        };

                        if (createOrderResult.orderInfo?.id) {
                            updateData[`syrveOrderIds.${venueName}`] = createOrderResult.orderInfo.id;
                        }

                        if (createOrderResult.correlationId) {
                            updateData[`syrveCorrelationIds.${venueName}`] = createOrderResult.correlationId;
                        }

                        await updateDoc(orderRef, updateData);
                    }
                }

                const pointsToAward = calculateLoyaltyPoints(venueTotal, await getLoyaltyPointsRate());
                if (pointsToAward > 0) {
                    const loyaltyResult = await awardLoyaltyPoints({
                        token,
                        organizationId,
                        phoneNumber: orderData.phoneNumber,
                        points: pointsToAward,
                        orderType: orderData.orderType,
                        firebaseOrderId: localStorage.getItem('firebaseOrderId'),
                        apiProxyBase: API_PROXY_BASE
                    });
                    if (loyaltyResult.success) {
                        const firebaseOrderId = localStorage.getItem('firebaseOrderId');
                        if (firebaseOrderId) {
                            await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                                [`loyaltyPointsAwarded.${venueName}`]: pointsToAward,
                                [`loyaltyAwardedAt.${venueName}`]: serverTimestamp(),
                                [`loyaltyCustomerId.${venueName}`]: loyaltyResult.customerId,
                                [`loyaltyWalletId.${venueName}`]: loyaltyResult.walletId
                            });
                        }
                    }
                }
            }

            const firebaseOrderId = localStorage.getItem('firebaseOrderId');
            if (firebaseOrderId) {
                try {
                    await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                        posSubmissionStatus: 'success',
                        posSubmittedAt: serverTimestamp()
                    });
                } catch (updateError) {
                    console.error('[FIREBASE] Failed to update success status:', updateError);
                }
            }
        } catch (error) {
            console.error("[SYRVE] Error creating Syrve orders:", error);

            const firebaseOrderId = localStorage.getItem('firebaseOrderId');
            if (firebaseOrderId) {
                try {
                    await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                        posSubmissionStatus: 'failed',
                        posSubmissionError: error.message,
                        posFailedAt: serverTimestamp()
                    });
                } catch (updateError) {
                    console.error('[FIREBASE] Failed to update error status:', updateError);
                }
            }

            throw new Error(`Syrve POS integration failed: ${error.message || 'Unknown error'}`);
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            fontFamily: 'Arial, sans-serif'
        }}>
            <h2>{processing ? '⏳' : searchParams.get('status') === 'success' ? '✅' : '❌'}</h2>
            <p>{message}</p>
            {processing && (
                <div style={{
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #3498db',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    animation: 'spin 1s linear infinite'
                }}></div>
            )}
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default PaymentReturn;
